<template>
  <v-btn @click="exportDataToExcel" color="green" dark outlined>
    <v-icon left>mdi-file-excel-outline</v-icon>Xuất Excel</v-btn
  >
</template>

<script>
import XLSX from 'xlsx-js-style'
import { ExamMark } from '../../../plugins/api'

export default {
  props: {
    room: Object,
    query: Object,
    exam: String
  },
  data() {
    return {
      loading: false,
      subjectHeaders: [],
      marks: []
    }
  },
  computed: {},
  mounted() {},
  methods: {
    async exportDataToExcel() {
      this.$loading.active = true
      const data = await ExamMark.getReportByGradeExcel(this.query)

      const workbook = XLSX.utils.book_new()
      const worksheet = XLSX.utils.json_to_sheet([], { skipHeader: true })

      // Define styles for the header rows
      const firstRowStyle = {
        font: { bold: true, sz: 13, name: 'Times New Roman' },
        alignment: { horizontal: 'left', vertical: 'center' }
      }
      const secondRowStyle = {
        font: { bold: true, sz: 18, name: 'Times New Roman' },
        alignment: { horizontal: 'center', vertical: 'center', wrapText: true }
      }

      // Add the School Name and Result header rows, merging them across all tables
      const columnsPerTable = data[0].reportData.length + 1
      let totalColumns = (columnsPerTable + 1) * data.length - 2

      worksheet['A1'] = { v: 'TRƯỜNG TH Lý Thái Tổ', t: 's', s: firstRowStyle }
      worksheet['!merges'] = worksheet['!merges'] || []
      worksheet['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 0, c: totalColumns } })

      worksheet['A2'] = {
        v: `BẢNG THỐNG KÊ KẾT QUẢ ${this.exam.toUpperCase()}\nNĂM HỌC 2024-2025`,
        t: 's',
        s: secondRowStyle
      }
      worksheet['!merges'].push({ s: { r: 1, c: 0 }, e: { r: 1, c: totalColumns } })
      worksheet['!rows'] = [{ hpx: 20 }, { hpx: 50 }]
      // Adjust starting row for tables to be row 3
      let currentColOffset = 0
      const headerFontStyle = {
        font: { bold: true, sz: 14, name: 'Times New Roman' },
        alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
        border: {
          top: { style: 'medium' },
          bottom: { style: 'medium' },
          left: { style: 'medium' },
          right: { style: 'medium' }
        }
      }
      const dataStyle = {
        font: { sz: 12, name: 'Times New Roman' },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' }
        }
      }
      const boldHeaderStyle = { ...dataStyle, font: { ...dataStyle.font, bold: true } }
      const subjectStyle = { ...dataStyle, font: { bold: true, sz: 12, name: 'Times New Roman' } }
      const highlightedStyle = {
        ...dataStyle,
        font: { bold: true, sz: 12, color: { rgb: 'FF0000' }, name: 'Times New Roman' }
      }

      const headers = this.getHeaders(data[0].reportData)

      data.forEach(gradeData => {
        const grade = gradeData.grade
        const reportData = gradeData.reportData

        // Merge cells for the grade title row
        worksheet['!merges'].push({
          s: { r: 2, c: currentColOffset },
          e: { r: 2, c: currentColOffset + columnsPerTable - 1 }
        })

        // Add the grade title across the top of each table
        for (let col = currentColOffset; col < currentColOffset + columnsPerTable; col++) {
          const titleCell = XLSX.utils.encode_cell({ r: 2, c: col })
          worksheet[titleCell] = { v: grade, t: 's', s: headerFontStyle }
        }

        headers.forEach((header, rowIndex) => {
          const headerCell = XLSX.utils.encode_cell({ r: rowIndex + 3, c: currentColOffset })
          let cellStyle = boldHeaderStyle
          if (header === 'Môn') cellStyle = subjectStyle
          else if (['Điểm cao nhất', 'SL HS đạt', 'Điểm TB của HS'].includes(header)) cellStyle = highlightedStyle

          worksheet[headerCell] = { v: header, t: 's', s: cellStyle }
        })

        reportData.forEach((dataItem, colIndex) => {
          headers.forEach((header, rowIndex) => {
            const cellAddress = XLSX.utils.encode_cell({ r: rowIndex + 3, c: currentColOffset + colIndex + 1 })
            worksheet[cellAddress] = { v: dataItem[header] != null ? dataItem[header] : '', t: 's', s: dataStyle }

            if (header === 'Môn') {
              worksheet[cellAddress].s = subjectStyle
            } else if (['Điểm cao nhất', 'SL HS đạt', 'Điểm TB của HS'].includes(header)) {
              worksheet[cellAddress].s = highlightedStyle
            }
          })
        })

        for (let col = currentColOffset; col < currentColOffset + columnsPerTable; col++) {
          const topCell = XLSX.utils.encode_cell({ r: 3, c: col })
          worksheet[topCell].s = {
            ...worksheet[topCell].s,
            border: { ...worksheet[topCell].s.border, top: { style: 'medium' } }
          }
        }
        for (let col = currentColOffset; col < currentColOffset + columnsPerTable; col++) {
          const bottomCell = XLSX.utils.encode_cell({ r: headers.length + 2, c: col })
          worksheet[bottomCell].s = {
            ...worksheet[bottomCell].s,
            border: { ...worksheet[bottomCell].s.border, bottom: { style: 'medium' } }
          }
        }
        for (let row = 3; row <= headers.length + 2; row++) {
          const leftCell = XLSX.utils.encode_cell({ r: row, c: currentColOffset })
          worksheet[leftCell].s = {
            ...worksheet[leftCell].s,
            border: { ...worksheet[leftCell].s.border, left: { style: 'medium' } }
          }
        }
        for (let row = 3; row <= headers.length + 2; row++) {
          const rightCell = XLSX.utils.encode_cell({ r: row, c: currentColOffset + columnsPerTable - 1 })
          worksheet[rightCell].s = {
            ...worksheet[rightCell].s,
            border: { ...worksheet[rightCell].s.border, right: { style: 'medium' } }
          }
        }

        currentColOffset += columnsPerTable + 1
      })

      worksheet['!ref'] = `A1:${XLSX.utils.encode_cell({ c: totalColumns, r: headers.length + 2 })}`
      worksheet['!cols'] = Array.from({ length: totalColumns + 1 }, () => ({ wch: 17 }))

      XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet 1')
      XLSX.writeFile(workbook, `Thống kê_${this.exam}_Theo khối.xlsx`)
      this.$loading.active = false
    },
    getHeaders(data) {
      const headers = [
        'Môn',
        'Điểm cao nhất',
        'SL HS đạt',
        '≥9',
        '8.0 đến <9.0',
        '7.0 đến <8.0',
        '6.0 đến <7.0',
        '5.0 đến <6.0',
        '4.0 đến <5.0',
        '3.0 đến <4.0',
        '2.0 đến <3.0',
        '≥1.0 đến <2.0',
        '≤1.0',
        'Số HS KT',
        'Số HS vắng KT',
        'Điểm TB của HS'
      ]

      return headers
    }
  },
  watch: {}
}
</script>
